<script setup lang="ts">
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';

interface Notification {
  uuid: string;
  title: string;
  message: string;
  orderUuid?: string;
  timestamp: number;
}

const props = defineProps<{
  notifications: Notification[];
}>();

const emit = defineEmits<{
  (e: 'dismiss', uuid: string): void;
}>();

const router = useRouter();
const visibleNotifications = ref<Notification[]>([]);

watch(() => props.notifications, (newNotifications) => {
  visibleNotifications.value = newNotifications;

  // Auto-dismiss after 5 seconds
  newNotifications.forEach(notification => {
    setTimeout(() => {
      dismissNotification(notification.uuid);
    }, 5000);
  });
}, { deep: true, immediate: true });

const handleNotificationClick = (notification: Notification) => {
  if (notification.orderUuid) {
    router.push(`/dashboard/view-order/${notification.orderUuid}`);
  }
  dismissNotification(notification.uuid);
};

const dismissNotification = (uuid: string) => {
  visibleNotifications.value = visibleNotifications.value.filter(n => n.uuid !== uuid);
  emit('dismiss', uuid);
};
</script>

<template>
<div class="toast-container">
  <transition-group name="toast" tag="div">
    <div v-for="notification in visibleNotifications" :key="notification.uuid" class="toast-notification"
      @click="handleNotificationClick(notification)">
      <div class="toast-icon">
        <v-icon color="white" size="32">mdi-package-variant</v-icon>
      </div>
      <div class="toast-content">
        <div class="toast-title">{{ notification.title }}</div>
        <div class="toast-message">{{ notification.message }}</div>
      </div>
      <v-btn icon size="x-small" variant="text" class="toast-close"
        @click.stop="dismissNotification(notification.uuid)">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>
  </transition-group>
</div>
</template>

<style scoped>
.toast-container {
  position: fixed;
  top: 90px;
  right: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 12px;
  pointer-events: none;
}

.toast-notification {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 360px;
  max-width: 480px;
  padding: 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  pointer-events: all;
  transition: all 0.3s ease;
  border-left: 4px solid #019972;
}

.toast-notification:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  transform: translateY(-2px);
}

.toast-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #019972 0%, #017a5a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-title {
  font-weight: 600;
  font-size: 15px;
  color: #21094a;
  margin-bottom: 4px;
}

.toast-message {
  font-size: 13px;
  color: #666;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.toast-close {
  flex-shrink: 0;
  opacity: 0.6;
}

.toast-close:hover {
  opacity: 1;
}

/* Animations */
.toast-enter-active {
  animation: slideInRight 0.3s ease-out;
}

.toast-leave-active {
  animation: slideOutRight 0.3s ease-in;
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }

  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slideOutRight {
  from {
    transform: translateX(0);
    opacity: 1;
  }

  to {
    transform: translateX(100%);
    opacity: 0;
  }
}

/* Responsive */
@media (max-width: 600px) {
  .toast-container {
    right: 12px;
    left: 12px;
  }

  .toast-notification {
    min-width: auto;
    max-width: 100%;
  }
}
</style>
